import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import moment from 'moment';

// material-ui
import {
    Avatar,
    Box,
    Button,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Link,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    useMediaQuery
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import DescriptionTwoToneIcon from '@mui/icons-material/DescriptionTwoTone';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import NavigateNextOutlinedIcon from '@mui/icons-material/NavigateNextOutlined';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Avatar_Custom from 'ui-component/extended/Avatar';
import SubCard from 'ui-component/cards/SubCard';
import { gridSpacing } from 'store/constant';
import DEFAULT_IMAGE from 'assets/images/default/image.png';
import useApi from 'hooks/useApi';
import VenueService from 'services/VenueService';
import TransactionService from 'services/TransactionService';
import { PAYMENT_MODE } from 'store/constant';
import CarouselImage from './Corousel';
import RevenueCard from 'ui-component/cards/RevenueCard';

// assets
import { IconEdit } from '@tabler/icons';

// third party
import PerfectScrollbar from 'react-perfect-scrollbar';
// import { date } from 'yup';

// progress
function LinearProgressWithLabel({ value, ...others }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center'
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    mr: 1
                }}
            >
                <LinearProgress value={value} {...others} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="textSecondary">{`${Math.round(value)}%`}</Typography>
            </Box>
        </Box>
    );
}
LinearProgressWithLabel.propTypes = {
    value: PropTypes.number
};

// styles
const CardWrapper = styled(MainCard)(({ theme }) => ({
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.light,
    overflow: 'hidden',
    position: 'relative',
    '&:after': {
        content: '""',
        position: 'absolute',
        width: 210,
        height: 210,
        background: `linear-gradient(210.04deg, ${theme.palette.primary[200]} -50.94%, rgba(144, 202, 249, 0) 83.49%)`,
        borderRadius: '50%',
        top: -30,
        right: -180
    },
    '&:before': {
        content: '""',
        position: 'absolute',
        width: 210,
        height: 210,
        background: `linear-gradient(140.9deg, ${theme.palette.primary[200]} -14.02%, rgba(144, 202, 249, 0) 77.58%)`,
        borderRadius: '50%',
        top: -160,
        right: -130
    }
}));

const VenueDetails = ({ venueUuid, venueObj, sportsTypes }) => {

    const theme = useTheme();
    // const backColor = theme.palette.background.paper;
    const primary = theme.palette.primary.main;
    const secondary = theme.palette.secondary.main;
    // const error = theme.palette.error.main;
    const info = theme.palette.info.main;
    const success = theme.palette.success.main;

    const successSX = { color: 'success.dark' };
    const matchDownXs = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const [currentDate, setCurrentDate] = useState(moment().format('DD-MM-YYYY'));
    const [bankDetails, setBankDetails] = useState({});
    const [venueStat, setVenueStat] = useState({
        active_time: 0,
        usage_time: 0,
        used_amount: 0,
        total_slot_amount: 0
    });
    const [slotCount, setSlotCount] = useState({
        total: 0,
        reserved: 0,
        confirmed: 0,
        cancelled: 0
    });
    const [settlementAmount, setSettlementAmount] = useState(0);
    const [settlementArray, setSettlementArray] = useState([]);

    const blockSX = {
        p: 2.5,
        borderLeft: '1px solid ',
        borderRight: '1px solid ',
        borderBottom: '1px solid ',
        borderLeftColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[200],
        borderBottomColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[200],
        borderRightColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[200]
    };

    const handleEditPage = () => {
        navigate(`/venue/edit/${venueUuid}`);
    };

    const getSportsType = function (typesString) {
        const splitTypes = typesString.split(',');
        return splitTypes.map(x => {
            let findInd = sportsTypes.findIndex(st => st.code === x);
            return findInd >= 0 ? sportsTypes[findInd].display_name : '';
        }).toString();
    };

    const getPaymentMode = function (modeString) {
        const splitTypes = modeString.split(',');
        return splitTypes.map(x => {
            let findInd = PAYMENT_MODE.findIndex(st => st.value === x);
            return PAYMENT_MODE[findInd].name;
        }).toString();
    };

    const formatSecondsToMinutes = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;

        const minutesLabel = minutes === 1 ? 'Min' : 'Mins';
        const secondsLabel = remainingSeconds === 1 ? 'Sec' : 'Secs';

        return `${minutes} ${minutesLabel} ${remainingSeconds.toString().padStart(2, '0')} ${secondsLabel}`;
    };

    const handleAddDays = (days) => {
        setCurrentDate(prevDate => moment(prevDate, 'DD-MM-YYYY').add(days, 'days').format('DD-MM-YYYY'));
    };

    const handleSubtractDays = (days) => {
        setCurrentDate(prevDate => moment(prevDate, 'DD-MM-YYYY').subtract(days, 'days').format('DD-MM-YYYY'));
    };

    const getBankHandler = (response) => {
        if (response.data.length) {
            setBankDetails(response.data[0]);
        }
    };

    const statisticsHandler = (response) => {
        setSlotCount(response.data.slot);
        setVenueStat(response.data);
    };

    const getTransferSettlementHandler = (response) => {
        const data = response.data;
        setSettlementAmount(data.settled_amount);
        setSettlementArray(data.bookings);
    };

    const getBankApi = useApi(VenueService.getBankAccountList, getBankHandler);
    const getVenueStatisticsApi = useApi(VenueService.getVenueStatistics, statisticsHandler);
    const getTransferSettlementApi = useApi(TransactionService.getTransferSettlement, getTransferSettlementHandler);

    useEffect(() => {
        const date = moment(currentDate, 'DD-MM-YYYY').format('YYYY-MM-DD');
        getBankApi.execute({ venueUuid });
        getVenueStatisticsApi.execute({ uuid_venue: venueUuid });
        getTransferSettlementApi.execute({ uuid_venue: venueUuid, date: date });
    }, []);

    useEffect(() => {
        const date = moment(currentDate, 'DD-MM-YYYY').format('YYYY-MM-DD');
        getTransferSettlementApi.execute({ uuid_venue: venueUuid, date: date });
    }, [currentDate]);

    return (
        <>
            {
                venueObj && <Grid container spacing={gridSpacing}>
                    <Grid container xs={12} lg={12}><Grid container spacing={gridSpacing} sx={{ marginTop: '3px', marginLeft: 0 }}>
                        <Grid item width='20%' >
                            <RevenueCard
                                primary="Total Active Time"
                                secondary={`${formatSecondsToMinutes(venueStat.active_time)}`}
                                iconPrimary={AccessTimeOutlinedIcon}
                                color={primary}
                            />
                        </Grid>
                        <Grid item width='20%'  >
                            <RevenueCard
                                primary="Total Usage Time"
                                secondary={`${formatSecondsToMinutes(venueStat.usage_time)}`}
                                iconPrimary={AccessTimeOutlinedIcon}
                                color={secondary}
                            />
                        </Grid>
                        <Grid item width='20%'  >
                            <RevenueCard
                                primary="Total Usage Amount"
                                secondary={`₹${venueStat.used_amount}`}
                                iconPrimary={CurrencyRupeeIcon}
                                color={success}
                            />
                        </Grid>
                        <Grid item width='20%' >
                            <RevenueCard
                                primary="Total Slot Bookings"
                                secondary={slotCount.total}
                                iconPrimary={DescriptionTwoToneIcon}
                                color={info}
                            />
                        </Grid>
                        <Grid item width='20%'  >
                            <RevenueCard
                                primary="Total Slot Bookings Amount"
                                secondary={`₹${venueStat.total_slot_amount}`}
                                iconPrimary={CurrencyRupeeIcon}
                                color={success}
                            />
                        </Grid>
                    </Grid></Grid>
                    <Grid item lg={6} xs={12}>
                        <SubCard
                            title={
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item >
                                        <Avatar_Custom alt="User 1" src={venueObj.banner_image ? venueObj.banner_image : DEFAULT_IMAGE} />
                                    </Grid>
                                    <Grid item xs zeroMinWidth>
                                        <Typography align="left" variant="subtitle1">
                                            {venueObj?.venue_name}
                                        </Typography>
                                        <Typography align="left" variant="subtitle1">
                                            {venueObj?.owner_name}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            }
                        >
                            <MainCard
                                content={false}
                                sx={{
                                    '& svg': {
                                        width: 50,
                                        height: 50,
                                        color: theme.palette.secondary.main,
                                        borderRadius: '14px',
                                        p: 1.25,
                                        bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.default : 'primary.light'
                                    }
                                }}
                            >
                                <Grid container alignItems="center" spacing={0}>
                                    <Grid item xs={12} sm={4} sx={blockSX}>
                                        <Grid
                                            container
                                            alignItems="center"
                                            spacing={1}
                                            justifyContent={matchDownXs ? 'space-between' : 'center'}
                                        >
                                            <Grid item>
                                                <AccessTimeFilledIcon />
                                            </Grid>
                                            <Grid item sm zeroMinWidth>
                                                <Typography variant="h5" align="center">
                                                    {slotCount.confirmed}
                                                </Typography>
                                                <Typography variant="subtitle2" align="center">
                                                    Venue Booked
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={blockSX}>
                                        <Grid
                                            container
                                            alignItems="center"
                                            spacing={1}
                                            justifyContent={matchDownXs ? 'space-between' : 'center'}
                                        >
                                            <Grid item>
                                                <AccessTimeFilledIcon />
                                            </Grid>
                                            <Grid item sm zeroMinWidth>
                                                <Typography variant="h5" align="center">
                                                    {slotCount.cancelled}
                                                </Typography>
                                                <Typography variant="subtitle2" align="center">
                                                    Slot Cancelled
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} sm={4} sx={blockSX}>
                                        <Grid
                                            container
                                            alignItems="center"
                                            spacing={1}
                                            justifyContent={matchDownXs ? 'space-between' : 'center'}
                                        >
                                            <Grid item>
                                                <AccessTimeFilledIcon />
                                            </Grid>
                                            <Grid item sm zeroMinWidth>
                                                <Typography variant="h5" align="center">
                                                    {slotCount.reserved}
                                                </Typography>
                                                <Typography variant="subtitle2" align="center">
                                                    Slot Reserved
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </MainCard>
                            {
                                venueObj.gallery_image && <CarouselImage imageArray={venueObj.gallery_image.split(',')} />
                            }
                        </SubCard>
                    </Grid>
                    <Grid item lg={6} xs={12}>
                        <Grid container spacing={gridSpacing}>
                            <Grid item xs={12}>
                                <SubCard
                                    title="About Arena"
                                    secondary={
                                        <Button aria-label="Edit Venue" onClick={handleEditPage}>
                                            <IconEdit stroke={1.5} size="20px" />
                                        </Button>
                                    }
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TableContainer>
                                                <Table
                                                    sx={{ '& td': { borderBottom: 'none' } }}
                                                    size="small"
                                                >
                                                    <TableBody>
                                                        <TableRow>
                                                            <TableCell variant="head">Sports Type</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>{venueObj.sports_type ? getSportsType(venueObj.sports_type) : ''}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell variant="head">Payment Mode</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>{venueObj.payment_mode ? getPaymentMode(venueObj.payment_mode) : ''}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell variant="head">Payment Mode</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>{venueObj.payment_mode ? getPaymentMode(venueObj.payment_mode) : ''}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell variant="head">Address</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>{venueObj?.address}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell variant="head">Location</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>
                                                                <Link
                                                                    underline='always'
                                                                    src={venueObj.venue_map_link}
                                                                    sx={{
                                                                        cursor: 'pointer'
                                                                    }}
                                                                >{venueObj.loc_city}</Link>
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell variant="head">Account Holder Name</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>{bankDetails.account_holder_name}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell variant="head">Bank Account Number</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>{bankDetails.bank_account_number}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell variant="head">Bank Name</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>{bankDetails.bank_name}</TableCell>
                                                        </TableRow>
                                                        <TableRow>
                                                            <TableCell variant="head">IFSC Code</TableCell>
                                                            <TableCell>:</TableCell>
                                                            <TableCell>{bankDetails.ifsc_code}</TableCell>
                                                        </TableRow>
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Grid>
                                    </Grid>
                                </SubCard>
                            </Grid>
                        </Grid>
                        <Grid container spacing={gridSpacing} sx={{ marginTop: '10px' }}>
                            <Grid item xs={12}>
                                <SubCard
                                    title="Today's Settlement"
                                >
                                    <CardWrapper border={false} content={false}>
                                        <Box sx={{ p: 2 }}>
                                            <List sx={{ py: 0 }}>
                                                <ListItem alignItems="center" disableGutters sx={{ py: 0 }}>
                                                    <ListItemAvatar>
                                                        <Avatar
                                                            variant="rounded"
                                                            sx={{
                                                                ...theme.typography.commonAvatar,
                                                                ...theme.typography.largeAvatar,
                                                                backgroundColor: theme.palette.primary[800],
                                                                color: '#fff'
                                                            }}
                                                        >
                                                            <TableChartOutlinedIcon fontSize="inherit" />
                                                        </Avatar>
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        sx={{
                                                            py: 0,
                                                            mt: 0.45,
                                                            mb: 0.45
                                                        }}
                                                        primary={
                                                            <Typography variant="h4" sx={{ color: '#fff' }}>
                                                                ₹{settlementAmount}
                                                            </Typography>
                                                        }
                                                        secondary={<>
                                                            <Typography variant="subtitle2" sx={{ color: '#fff', mt: 0.25 }}>
                                                                Todays's Settlements
                                                            </Typography>
                                                        </>

                                                        }
                                                    />
                                                    <ListItemText
                                                        sx={{
                                                            py: 0,
                                                            mt: 0.45,
                                                            mb: 0.45
                                                        }}
                                                        primary={
                                                            <Grid sx={{ display: "flex" }}>
                                                                <Typography variant="h4" sx={{ color: '#fff' }}>
                                                                    <IconButton sx={{ color: 'primary.light', mt: 0.25, mr: 3 }}>
                                                                        <NavigateBeforeOutlinedIcon color='primary.light' onClick={() => handleSubtractDays(1)} />
                                                                    </IconButton>
                                                                </Typography>
                                                                <Typography variant="h4" sx={{ color: '#fff', mt: 1.5 }}>
                                                                    {currentDate}
                                                                </Typography>
                                                                <Typography variant="h4" sx={{ color: 'primary.light' }}>
                                                                    <IconButton sx={{ color: 'primary.light', mt: 0.25, ml: 3 }}>
                                                                        <NavigateNextOutlinedIcon color='primary.light' onClick={() => handleAddDays(1)} />
                                                                    </IconButton>
                                                                </Typography>
                                                            </Grid>
                                                        }
                                                    />
                                                </ListItem>
                                            </List>
                                        </Box>
                                    </CardWrapper>
                                    <PerfectScrollbar style={{ height: 370 }}>
                                        <List
                                            component="nav"
                                            aria-label="main mailbox folders"
                                            sx={{
                                                '& svg': {
                                                    width: 32,
                                                    my: -0.75,
                                                    ml: -0.75,
                                                    mr: 0.75
                                                }
                                            }}
                                        >
                                            {
                                                settlementArray.map((bookingObj, index) =>
                                                    <>
                                                        <ListItemButton>
                                                            {/* <ListItemIcon>
                                                            <ArrowDropUpIcon sx={successSX} />
                                                        </ListItemIcon> */}
                                                            <ListItemText
                                                                primary={
                                                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                        <span>{bookingObj.slot_id}</span>
                                                                        <Typography sx={successSX}>+ ₹{bookingObj.transfer_amount}</Typography>
                                                                    </Stack>
                                                                }
                                                            />
                                                        </ListItemButton>
                                                        <Divider />
                                                    </>
                                                )
                                            }
                                        </List>
                                    </PerfectScrollbar>
                                </SubCard>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid >
            }
        </>
    );
};

export default VenueDetails;
